import useHttpProvider from "@/common/hooks/use-http";
import { notifyError, notifySuccess } from "@/components/ui/sonner";
import { Plan } from "@/features/payments/models/plan";
import { Subscription } from "@/features/payments/models/subscription";
import { buildCheckoutPlanUrl } from "@/features/payments/route";
import * as SubscriptionServices from "@/features/payments/services/subscriptions";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

function useManageMembership() {
	const { http } = useHttpProvider();

	const navigate = useNavigate();
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);

	const [showCancelSubscriptionDialog, setShowCancelSubscriptionDialog] =
		useState<boolean>(false);
	const [showModifySubscriptionDialog, setShowModifySubscriptionDialog] =
		useState<boolean>(false);

	const [upcomingPlan, setUpcomingPlan] = useState<Plan | null>(null);
	const [currentPlan, setCurrentPlan] = useState<Plan | null>(null);
	const [currentSubscription, setCurrentSubscription] =
		useState<Subscription | null>(null);

	function onPlanSelected(plan: Plan) {
		setUpcomingPlan(plan);

		if (!currentSubscription) {
			navigate(buildCheckoutPlanUrl(plan.id), { state: { plan } });
		} else {
			setShowModifySubscriptionDialog(true);
		}
	}

	async function onCancelSubscription() {
		try {
			await SubscriptionServices.cancelSubscription(
				http,
				currentSubscription!.subscriptionId
			);

			setShowCancelSubscriptionDialog(false);
			getUserActiveSubscription();

			notifySuccess(
				"Subscription cancelled successfully",
				"Your subscription has been cancelled successfully."
			);
		} catch {
			notifyError(
				"Failed to cancel subscription",
				"An error occurred while cancelling your subscription."
			);
		}
	}

	async function onModifySubscription() {
		try {
			await SubscriptionServices.modifySubscription(http, upcomingPlan!.id);

			setShowModifySubscriptionDialog(false);
			getUserActiveSubscription();

			notifySuccess(
				"Subscription modified successfully",
				"Your subscription has been modified successfully."
			);
		} catch (error: unknown) {
			const detail =
				typeof error === "object" && error !== null && "detail" in error
					? (error as { detail?: string }).detail
					: undefined;

			if (detail === "region_not_allowed") {
				notifyError(
					"Not available in your region",
					"Subscriptions can't be modified from your region."
				);
				return;
			}

			notifyError(
				"Failed to modify subscription",
				"An error occurred while modifying your subscription."
			);
		}
	}

	async function getUserActiveSubscription() {
		setCurrentPlan(null);
		setCurrentSubscription(null);

		const { plan, subscription } =
			await SubscriptionServices.getUserActiveSubscription(http);

		setCurrentPlan(plan);
		setCurrentSubscription(subscription);

		const preSelectedPlan = location.state?.plan;
		if (preSelectedPlan && subscription) {
			setShowModifySubscriptionDialog(true);
		} else if (preSelectedPlan && !subscription) {
			navigate(buildCheckoutPlanUrl(preSelectedPlan.id), {
				state: { plan: preSelectedPlan },
			});
		}
	}

	function checkForSuccessfulBoughtPlan() {
		const sessionId = queryParams.get("session_id");

		if (sessionId) {
			notifySuccess(
				"Subscription bought successfully",
				"Your subscription has been bought successfully."
			);
		}
	}

	useEffect(() => {
		checkForSuccessfulBoughtPlan();
		getUserActiveSubscription();
	}, []);

	return {
		currentSubscription,
		currentPlan,
		onPlanSelected,

		showModifySubscriptionDialog,
		setShowModifySubscriptionDialog,
		onModifySubscription,

		toggleShowCancelSubscriptionDialog: () =>
			setShowCancelSubscriptionDialog(!showCancelSubscriptionDialog),
		showCancelSubscriptionDialog,
		setShowCancelSubscriptionDialog,
		onCancelSubscription,
	};
}

export default useManageMembership;
